import {LocalVideoOptionsArray, BackgroundVideoOptionsArray} from '../../../types';
import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import {MouseHijack} from '../../wraps';
import {RatioKeptVideo} from './RatioKeptVideo';
import {Typography} from '../typography';
import Grid from '@material-ui/core/Grid';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {VerticalSpacer} from '../spacer';
import {CenteredGrid, GridContainer} from '../grid';
import {isValidMediaOptions} from '../../../utils';
import {VideoGalleryList} from './VideoGalleryList';
import {Eyebrow} from '../eyebrow';
import {LeftAlignedTextBlockProps} from '../text';

const Styled = styled.div<{moreThan5Videos?: boolean}>`
    .ue-video-gallery {
        .MuiGrid-item {
            align-self: flex-start;
        }

        .main-video,
        .other-videos-section {
            align-self: self-start;
        }

        .other-videos-section {
            background-color: ${(p) =>
                p.theme.ifLight(
                    p,
                    p.theme.palette.neutral.chalk,
                    p.theme.palette.neutral.concrete
                )};

            .other-videos-gradient-overlay {
                pointer-events: none;
            }

            ${(p) =>
                p.moreThan5Videos &&
                `
            .other-videos-gradient-overlay {
                background: linear-gradient(180deg, rgba(14, 17, 40, 0) 0%, ${p.theme.ifLight(
                    p,
                    p.theme.palette.neutral.stone,
                    p.theme.palette.neutral.concrete
                )} 95.69%);
                z-index: 1;
                height: 96px;
                position: relative;
                bottom: 96px;
            }`};

            ${(p) => p.theme.breakpoints.up('md')} {
                height: ${(p) => (p.moreThan5Videos ? '630px' : 'initial')};

                .video-gallery-videos-list {
                    height: ${(p) => (p.moreThan5Videos ? '630px' : '500px')};

                    overflow-y: auto;
                    overflow-x: hidden;
                }
            }
        }
    }

    .video-gallery-videos-list {
        ::-webkit-scrollbar {
            width: 6px;
            background-color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.neutral.pearl, p.theme.palette.neutral.steel)};
        }

        ::-webkit-scrollbar-thumb {
            border-radius: 9999px;
            background-color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.neutral.charcoal, p.theme.palette.common.black)};
            outline: 1px solid
                ${(p) =>
                    p.theme.ifLight(
                        p,
                        p.theme.palette.neutral.charcoal,
                        p.theme.palette.common.black
                    )};
        }
    }

    .video-caption {
        text-align: right;
        h4,
        .paragraph {
            display: inline-block;
        }
        h4 {
            margin-right: 8px;
        }
    }
    .video-wrap {
        background-color: #000;
        .video-wrapper {
            background-color: #000;
        }
    }

    .video-description {
        padding-right: 20px;
    }

    .video-eyebrow,
    .image-and-textcard-sm-eyebrow {
        color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.neutral.charcoal, p.theme.palette.neutral.stone)};
    }

    .container {
        width: 100%;
        display: flex;
        flex-shrink: 1;
    }
    .left-sidebar {
        min-width: 66%;
        max-width: 900px;

        video {
            width: 100%;
            height: 100%;
        }
    }

    .right-sidebar {
        overflow-y: auto;
        aspect-ratio: 80/45;

        background-color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.neutral.chalk, p.theme.palette.neutral.concrete)};
    }

    .nav {
        padding-left: 0;
        color: white;
    }

    .nav > li {
        list-style-type: none;
    }
`;

export interface VideoGalleryVideoProps {
    mainContentOptions?: LocalVideoOptionsArray;
    mediaOptions?: BackgroundVideoOptionsArray;
    descriptionTextBlocks: LeftAlignedTextBlockProps;
    caption?: string;
    label?: string;
    useIcons?: boolean;
    mute?: boolean;
}

export interface VideoGalleryProps {
    label?: string;
    caption?: string;
    accentColor?: AccentColorOptions;
    title?: string;
    desc?: string;
    videosList?: VideoGalleryVideoProps[];
}

export const VideoGallery = ({accentColor, videosList}: VideoGalleryProps): JSX.Element | null => {
    const isSm = useBreakpointDown('sm');
    const [activated, setActivated] = useState(false);
    const [selectedVideoID, setSetSelectedVideoID] = useState(0);
    const [currentVideo, setCurrentVideo] = useState(videosList && videosList[0]);

    const moreThan5Videos = videosList && videosList.length >= 5;

    const play = useCallback(() => {
        setActivated(true);
    }, [setActivated]);
    const handleKeyUp = useCallback(
        (e) => {
            if (e.key === 'Enter' || e.key === 'Space' || e.key === ' ') {
                setActivated(true);
            }
        },
        [setActivated]
    );

    const selectVideo = (video: VideoGalleryVideoProps, selectVideoIndex: number) => {
        setActivated(false);
        setSetSelectedVideoID(selectVideoIndex);
        video?.mediaOptions && setCurrentVideo(video);
    };

    if (
        !currentVideo?.mainContentOptions ||
        !currentVideo?.mainContentOptions.length ||
        !isValidMediaOptions(currentVideo?.mediaOptions)
    ) {
        return null;
    }

    const mainVideo = (
        <>
            {activated && currentVideo ? (
                <RatioKeptVideo
                    key="embed-video-activated"
                    mediaOptions={currentVideo?.mediaOptions}
                    mute={currentVideo?.mute}
                    forcePlay={true}
                />
            ) : (
                <MouseHijack
                    onClick={play}
                    hoverCursorType="play"
                    useIcons={currentVideo?.useIcons}
                    accentColor={accentColor}>
                    <RatioKeptVideo
                        key="embed-video-placeholder"
                        mediaOptions={currentVideo?.mainContentOptions}
                        mute={currentVideo?.mute}
                    />
                </MouseHijack>
            )}

            {currentVideo?.caption && (
                <div className="video-caption">
                    <VerticalSpacer variant="custom" spacing={1} />
                    {currentVideo?.label && (
                        <Typography variant="h4">{currentVideo?.label}</Typography>
                    )}
                    {currentVideo?.caption && (
                        <Typography variant="p" caption>
                            {currentVideo?.caption}
                        </Typography>
                    )}
                </div>
            )}
        </>
    );

    const videoDescriptionTextBlocks = (
        <Grid container alignItems="flex-start">
            {
                /*6 col Headline (of total Gallery cols) when no eyebrow or description*/
                !currentVideo?.descriptionTextBlocks?.eyebrow &&
                    !currentVideo?.descriptionTextBlocks?.description && (
                        <Grid item xs={12} md={9}>
                            {currentVideo?.descriptionTextBlocks?.headline && (
                                <>
                                    <Typography variant="h3" big>
                                        {currentVideo?.descriptionTextBlocks?.headline}
                                    </Typography>
                                    <VerticalSpacer variant="custom" downSmSpacing={2} />
                                </>
                            )}
                        </Grid>
                    )
            }
            <Grid className="video-eyebrow" item xs={12}>
                {currentVideo?.descriptionTextBlocks?.eyebrow &&
                    currentVideo?.descriptionTextBlocks?.eyebrow.text && (
                        <>
                            <Eyebrow big {...currentVideo?.descriptionTextBlocks?.eyebrow} />
                            <VerticalSpacer variant="eyebrow" />
                        </>
                    )}
            </Grid>
            <Grid item xs={12} md={5}>
                {currentVideo?.descriptionTextBlocks?.headline && (
                    <>
                        <Typography variant="h3" big>
                            {currentVideo?.descriptionTextBlocks?.headline}
                        </Typography>
                        <VerticalSpacer variant="custom" downSmSpacing={2} />
                    </>
                )}
            </Grid>
            <Grid item md={1}>
                &nbsp;
            </Grid>
            {currentVideo?.descriptionTextBlocks?.description && (
                <Grid className="video-description" item xs={12} md={6}>
                    <Typography variant="p">
                        {currentVideo?.descriptionTextBlocks?.description}
                    </Typography>
                </Grid>
            )}
        </Grid>
    );

    const fourOrLessVideosVariant = (
        <>
            <div className="container">
                <div className="left-sidebar">{mainVideo}</div>
                <div className="right-sidebar video-gallery-videos-list">
                    {
                        <VideoGalleryList
                            videosList={videosList}
                            selectVideo={selectVideo}
                            selectedVideoID={selectedVideoID}
                        />
                    }
                </div>
            </div>{' '}
            <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                <Grid
                    className="video-description-section"
                    item
                    xs={12}
                    sm={12}
                    md={8}
                    onKeyUp={handleKeyUp}>
                    {isSm ? (
                        <VerticalSpacer variant="custom" spacing={4} />
                    ) : (
                        <VerticalSpacer variant="custom" spacing={3} />
                    )}
                    {videoDescriptionTextBlocks}
                    {isSm && <VerticalSpacer variant="custom" spacing={4} />}
                </Grid>
                <Grid className="other-videos-section" item xs={12} sm={12} md={4}></Grid>
            </Grid>
        </>
    );

    const fiveOrMoreVideosVariant = (
        <Grid
            className="ue-video-gallery"
            container
            direction="row"
            justifyContent="center"
            alignItems="center">
            <Grid className="main-video" item xs={12} sm={12} md={8} onKeyUp={handleKeyUp}>
                {mainVideo}
                {isSm ? (
                    <VerticalSpacer variant="custom" spacing={4} />
                ) : (
                    <VerticalSpacer variant="custom" spacing={3} />
                )}
                {videoDescriptionTextBlocks}
                {isSm && <VerticalSpacer variant="custom" spacing={4} />}
            </Grid>
            <Grid className="other-videos-section" item xs={12} sm={12} md={4}>
                <VideoGalleryList
                    videosList={videosList}
                    selectVideo={selectVideo}
                    selectedVideoID={selectedVideoID}
                />

                {!isSm && <div className="other-videos-gradient-overlay">&nbsp;</div>}
            </Grid>
        </Grid>
    );

    return (
        <Styled className="embedded-video" moreThan5Videos={moreThan5Videos}>
            <GridContainer>
                <CenteredGrid>
                    {moreThan5Videos ? fiveOrMoreVideosVariant : fourOrLessVideosVariant}
                </CenteredGrid>
            </GridContainer>
        </Styled>
    );
};
